import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { authenticate, clearSession, getSession } from './authStore';
import type { Session } from './authStore';
import { bootstrapPortalStore } from '../data/portalStore';

interface AuthContextValue {
  session: Session | null;
  login: (username: string, password: string) => Session | null;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(() => getSession());

  useEffect(() => {
    bootstrapPortalStore();
  }, []);

  const login = (username: string, password: string) => {
    const nextSession = authenticate(username, password);
    setSession(nextSession);
    return nextSession;
  };

  const logout = () => {
    clearSession();
    setSession(null);
  };

  const value = useMemo(() => ({ session, login, logout }), [session]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
